
// Fælles label-tilstand uden afhængigheder (importeres af FeatureVisual og LabelManager).
//
// Skel mellem brugerens valg og automatisk skjulning (pr. label, i label.userData):
//   pvWanted  brugerens valg (labelsVisible / resultLabelVisible) - ejes af FeatureVisual, ændres aldrig af LabelManager
//   pvPeek    midlertidig visning (hover, aktiv oprettelse/redigering) af en label, brugeren ikke har tændt
//   pvAuto    skjult automatisk (zoom/plads) - ejes af LabelManager
// label.visible = (pvWanted || pvPeek) && !pvAuto. Eksplicit skjulte labels (pvWanted falsk) vises derfor kun som peek.

import {LEVEL} from "./labelLayout.js";
import {ScreenSpaceSizes} from "../ScreenSpaceScale.js";

// Teksturens canvas-px -> skærm-px: sprite.scale = canvas * 0.01 lokale enheder, og een lokal enhed er ScreenSpaceSizes.label px
export const SPRITE_PX = 0.01 * ScreenSpaceSizes.label;
export const PX_PER_LOCAL = ScreenSpaceSizes.label;

let revision = 1;

// Alt, der kan ændre en labels indhold, anker, synlighed eller prioritet, kalder dette; LabelManager lægger kun layout om, når tallet er ændret.
export function bumpLabelRevision(){
	revision++;
}

export function getLabelRevision(){
	return revision;
}

export function applyLabelVisibility(label){
	const u = label.userData;
	label.visible = !!((u.pvWanted || u.pvPeek) && !u.pvAuto);
}

export function setLabelWanted(label, wanted){
	const u = label.userData;
	wanted = !!wanted;
	if(u.pvWanted === wanted && label.visible === !!((wanted || u.pvPeek) && !u.pvAuto)) return;
	u.pvWanted = wanted;
	applyLabelVisibility(label);
	revision++;
}

// Labelens funktion (rolle), erklæret af ejeren: "result" = værktøjets samlede resultatboks. LabelManager klassificerer efter rollen, ikke efter hvilken
// klasse der ejer labelen; punktkoordinater, delmål og hjælpeværdier har egne labels og egne regler.
export const LABEL_ROLE_RESULT = "result";

export function setLabelRole(label, role){
	label.userData.pvRole = role;
}

export function labelRole(label){
	return label.userData.pvRole;
}

// Ankeret (verdensposition), som EJEREN har valgt til en label. LabelManager kan flytte selve label.position til et andet punkt på samme
// objekt, når ankeret ligger uden for billedet (setLabelAnchorOverride); ejerens eget anker huskes i userData.pvHome og vender tilbage,
// så snart det igen er synligt eller labelen ikke længere styres. Ejere skriver altid via setLabelAnchor, aldrig direkte til label.position.
export function setLabelAnchor(label, x, y, z){
	const u = label.userData;
	if(!u.pvHome) u.pvHome = {x, y, z};
	else{ u.pvHome.x = x; u.pvHome.y = y; u.pvHome.z = z; }
	if(!u.pvOverride) label.position.set(x, y, z);
	revision++;
}

export function labelHome(label){
	return label.userData.pvHome || label.position;
}

export function setLabelAnchorOverride(label, x, y, z){
	const u = label.userData;
	if(!u.pvHome) u.pvHome = {x: label.position.x, y: label.position.y, z: label.position.z};
	u.pvOverride = true;
	label.position.set(x, y, z);
}

export function clearLabelAnchorOverride(label){
	const u = label.userData;
	if(!u.pvOverride) return;
	u.pvOverride = false;
	if(u.pvHome) label.position.set(u.pvHome.x, u.pvHome.y, u.pvHome.z);
}

// Indholdstrappe: ladder = [null, T1 (mindste), T2 (reduceret), T3 (fuld)]; null = trinnet findes ikke (LabelManager tilbyder det ikke).
// Tekst til et trin uden egen tekst er teksten fra nærmeste højere trin.
export function ladderText(ladder, level){
	for(let i = level; i <= LEVEL.FULL; i++) if(ladder[i] != null) return ladder[i];
	for(let i = level - 1; i >= LEVEL.COMPACT; i--) if(ladder[i] != null) return ladder[i];
	return "";
}

// en enkeltlinjet label med kopi-ikon får ekstra plads i højre side, så ikonet ikke dækker teksten
function padFor(u, text){
	return (u.pvIconPad && text.indexOf("\n") < 0) ? u.pvIconPad : 0;
}

export function setLabelLadder(label, ladder){
	const u = label.userData;
	u.pvLadder = ladder;
	const level = u.pvLevel === undefined ? LEVEL.FULL : u.pvLevel;
	const text = ladderText(ladder, level);
	const pad = padFor(u, text);
	if(label.text !== text || label.extraRight !== pad){
		label.setText(text, pad);
		revision++;
	}
}

// full: indholdet ved fuldt trin; compact: mindste trin (uden compact bruges full overalt).
export function setLabelContent(label, full, compact){
	setLabelLadder(label, [null, compact == null ? full : compact, null, full]);
}

export function setLabelLevel(label, level){
	const u = label.userData;
	if(u.pvLevel === level) return;
	u.pvLevel = level;
	if(!u.pvLadder) return;
	const text = ladderText(u.pvLadder, level);
	label.setText(text, padFor(u, text));
}

// Lægger den skærm-forskydning (px) LabelManager har valgt oven på ankeret ved at flytte selve sprite'n i labelens lokale rum
// (lokal enhed = ScreenSpaceSizes.label px, uafhængigt af afstanden). right/up er kameraets akser i verden.
// axes: {right, up, forward} (kameraets akser i verden). I perspektiv svarer en lokal enhed til PX_PER_LOCAL * f px, hvor f = afstand / dybde.
export function applyLabelOffset(label, axes, camera){
	const u = label.userData;
	const p = label.sprite.position;
	if(!u.pvDx && !u.pvDy){
		if(p.x !== 0 || p.y !== 0 || p.z !== 0) p.set(0, 0, 0);
		return;
	}
	const right = axes.right, up = axes.up;
	let f = 1;
	if(!camera.isOrthographicCamera){
		const lp = label.position, cp = camera.position, fw = axes.forward;
		const dx = lp.x - cp.x, dy = lp.y - cp.y, dz = lp.z - cp.z;
		const depth = dx * fw.x + dy * fw.y + dz * fw.z;
		if(depth > 1e-6) f = Math.sqrt(dx * dx + dy * dy + dz * dz) / depth;
	}
	const kx = u.pvDx / (PX_PER_LOCAL * f), ky = -u.pvDy / (PX_PER_LOCAL * f);
	p.set(right.x * kx + up.x * ky, right.y * kx + up.y * ky, right.z * kx + up.z * ky);
}

// Nulstiller alt, LabelManager har lagt på en label (bruges, når den ikke længere styres).
export function resetLabelManagement(label){
	const u = label.userData;
	clearLabelAnchorOverride(label);
	u.pvPeek = false;
	u.pvAuto = false;
	u.pvDx = 0;
	u.pvDy = 0;
	u.pvLeader = false;
	if(u.pvLevel !== undefined && u.pvLevel !== LEVEL.FULL){
		u.pvLevel = LEVEL.FULL;
		if(u.pvLadder && label.parent){
			const text = ladderText(u.pvLadder, LEVEL.FULL);
			label.setText(text, padFor(u, text));
		}
	}
	label.sprite.position.set(0, 0, 0);
	label.sprite.renderOrder = 10;
	applyLabelVisibility(label);
}
